<template>
  <span
    class="text-static-default-hi pointer-events-none inline-flex items-center"
  >
    <span>{{ fixedText }}</span>
    <span
      :key="idx"
      class="ml-3 inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap bg-[linear-gradient(to_left,transparent_0%,transparent_33%,var(--gradient-color)_50%,#0e1016_66%,#0e1016_100%)] bg-[length:300%_100%] bg-clip-text text-transparent [-webkit-text-fill-color:transparent]"
      :class="isDeleting ? 'gradient-hide' : 'gradient-reveal'"
      :style="{
        '--show-duration': showDuration + 'ms',
        '--hide-duration': hideDuration + 'ms',
        '--gradient-color': currentColor,
      }"
    >
      {{ dynamicText }}
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

const {
  translations = [],
  displayDuration = 2500,
  showDuration = 1500,
  hideDuration = 500,
} = defineProps<{
  translations: string[]
  displayDuration?: number
  showDuration?: number
  hideDuration?: number
}>()

const cleanTranslations = translations.filter((str) => str && str !== '')
const fixedText = computed(() => getCommonPrefix(cleanTranslations))
const suffixes = computed(() =>
  cleanTranslations.map((t) => t.slice(fixedText.value.length)),
)

const idx = ref(0)
const dynamicText = ref(suffixes.value[0] ?? '')
const isDeleting = ref(false)

const colors = ['#7B3DBE', '#3F55BF', '#9F3963']
let currentColor = colors[0]

function getRandomColor() {
  let newColor
  do {
    newColor = colors[Math.floor(Math.random() * colors.length)]
  } while (newColor === currentColor)
  currentColor = newColor

  return newColor
}

const nextTextTimeout = ref<number | null>(null)
const loopTimeout = ref<number | null>(null)

function nextText() {
  isDeleting.value = true
  nextTextTimeout.value = window.setTimeout(() => {
    idx.value = (idx.value + 1) % suffixes.value.length
    dynamicText.value = suffixes.value[idx.value]
    getRandomColor()
    isDeleting.value = false
  }, hideDuration)
}

function startLoop() {
  nextText()
  loopTimeout.value = window.setTimeout(
    startLoop,
    displayDuration + showDuration + hideDuration,
  )
}

onMounted(() => {
  loopTimeout.value = window.setTimeout(startLoop, displayDuration)
})
onUnmounted(() => {
  if (nextTextTimeout.value !== null) clearTimeout(nextTextTimeout.value)
  if (loopTimeout.value !== null) clearTimeout(loopTimeout.value)
})

function getCommonPrefix(strings: string[]) {
  if (!strings.length) return ''

  return strings.reduce((prefix, str) => {
    let i = 0
    while (i < prefix.length && i < str.length && prefix[i] === str[i]) i += 1

    return prefix.slice(0, i)
  }, strings[0])
}
</script>

<style scoped>
@keyframes gradient-reveal {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: 0% 0;
  }
}

@keyframes gradient-hide {
  0% {
    background-position: 0% 0;
  }
  100% {
    background-position: 100% 0;
  }
}

.gradient-reveal {
  animation: gradient-reveal var(--show-duration) ease forwards;
}

.gradient-hide {
  animation: gradient-hide var(--hide-duration) ease forwards;
}
</style>
